import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import TinyMCEEditor from '../../components/TinyMCEEditor';
import ImageUploadField from '../../components/ImageUploadField';
import { CBroker, CConfig } from './types';

/**
 * Per-broker content config editor. Centralizes what today lives in each portal repo
 * (ftwp broker-data-<slug>.php / BEX $broker_content_config): the article disclaimer,
 * newsletter disclosure, risk %, affiliate URL, logo, profile slug, CTA USPs. Emitted in
 * every portal's feed so the disclaimer/CTA stop being per-repo code.
 */
const emptyConfig = (brokerId: number): CConfig => ({
  id: 0,
  broker_id: brokerId,
  source_name: null,
  article_disclaimer: null,
  newsletter_disclosure: null,
  article_cta_usps: [],
  risk_percentage: null,
  affiliate_url: null,
  logo_url: null,
  profile_urlsegment: null,
});

const BrokerConfigDialog: React.FC<{
  broker: CBroker | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ broker, onClose, onSaved }) => {
  const [config, setConfig] = useState<CConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!broker) {
      setConfig(null);
      return;
    }
    (async () => {
      const res = await fetchData<CConfig | null>(`/api/broker-content/config/${broker.id}`);
      if (!cancelled) {
        if (res.success) setConfig(res.data ?? emptyConfig(broker.id));
        else setError(res.error || 'Konfiguration konnte nicht geladen werden');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [broker]);

  const setField = (key: keyof CConfig, value: unknown) =>
    setConfig((prev) => (prev ? { ...prev, [key]: value } : prev));

  const save = async () => {
    if (!config || !broker) return;
    setSaving(true);
    setError(null);
    const res = await fetchData(`/api/broker-content/config/${broker.id}`, {
      method: 'PUT',
      body: JSON.stringify({
        source_name: config.source_name,
        article_disclaimer: config.article_disclaimer,
        newsletter_disclosure: config.newsletter_disclosure,
        article_cta_usps: (config.article_cta_usps || []).filter((s) => s && s.trim()),
        risk_percentage: config.risk_percentage,
        affiliate_url: config.affiliate_url,
        logo_url: config.logo_url,
        profile_urlsegment: config.profile_urlsegment,
      }),
    });
    setSaving(false);
    if (res.success) onSaved();
    else setError(res.error || 'Speichern fehlgeschlagen');
  };

  return (
    <Dialog open={!!broker} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Broker-Konfiguration{broker ? ` — ${broker.name}` : ''}</DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        {config && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <TinyMCEEditor
                label="Risikohinweis (Artikel)"
                value={config.article_disclaimer || ''}
                onChange={(html) => setField('article_disclaimer', html)}
                height={240}
              />
              <Typography variant="caption" color="text.secondary">
                Wird unter jedem Broker-Artikel ausgeliefert (ersetzt broker-data-*.php).
                Pflichtangaben (Werbemitteilung, §85 WpHG, Risikohinweis) nicht entfernen.
              </Typography>
            </Box>
            <TextField
              label="Newsletter-Offenlegung"
              value={config.newsletter_disclosure || ''}
              onChange={(e) => setField('newsletter_disclosure', e.target.value)}
              multiline
              minRows={2}
              size="small"
            />
            <TextField
              label="Risiko %"
              value={config.risk_percentage || ''}
              onChange={(e) => setField('risk_percentage', e.target.value)}
              size="small"
            />
            <TextField
              label="Affiliate-URL"
              value={config.affiliate_url || ''}
              onChange={(e) => setField('affiliate_url', e.target.value)}
              size="small"
            />
            <ImageUploadField
              label="Broker-Logo"
              uploadUrl="/api/broker-content/upload-image?kind=logos"
              value={config.logo_url}
              onChange={(url) => setField('logo_url', url)}
            />
            <Box>
              <TextField
                fullWidth
                label="Profil-Slug (URL-Segment)"
                value={config.profile_urlsegment || ''}
                onChange={(e) => setField('profile_urlsegment', e.target.value)}
                size="small"
              />
              {/^https?:\/\//i.test(config.profile_urlsegment || '') && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  <a href={config.profile_urlsegment as string} target="_blank" rel="noreferrer">
                    Broker-Profil ansehen ↗
                  </a>
                </Typography>
              )}
            </Box>
            <TextField
              label="CTA USPs (eine pro Zeile)"
              value={(config.article_cta_usps || []).join('\n')}
              onChange={(e) => setField('article_cta_usps', e.target.value.split('\n'))}
              multiline
              minRows={3}
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={save} disabled={saving || !config}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrokerConfigDialog;
